import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { AFMessage } from '../stores/data/types';

interface UseWebSocketOptions {
  url: string;
  onTokenUpdateMessage?: (message: AFMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  healthCheckInterval?: number; // 헬스체크 간격 (밀리초)
}

export const useWebSocket = ({
  url,
  onTokenUpdateMessage,
  onOpen,
  onClose,
  onError,
  reconnectInterval = 10000, // 10초로 증가
  maxReconnectAttempts = 500,
  healthCheckInterval = 20000 // 20초마다 헬스체크
}: UseWebSocketOptions) => {
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const isDestroyedRef = useRef(false);
  const lastErrorRef = useRef<number>(0);
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageTimeRef = useRef<number>(Date.now());
  const [isConnected, setIsConnected] = useState(false);

  const connect = useCallback(() => {
    // 컴포넌트가 언마운트되었거나 이미 연결 중이면 중단
    if (isDestroyedRef.current || isConnectingRef.current) {
      console.log('🚫 연결 중단: 컴포넌트 언마운트 또는 이미 연결 중');
      return;
    }

    // 최대 재연결 시도 횟수 초과 시 중단
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('🚫 최대 재연결 시도 횟수 초과, 재연결 중단');
      return;
    }

    // 에러 발생 후 최소 대기 시간 (5초)
    const now = Date.now();
    if (now - lastErrorRef.current < 5000) {
      console.log('⏳ 에러 쿨다운 대기 중...');
      return;
    }

    console.log(`🔌 Socket.IO 연결 시도 ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts + 1}:`, url);
    isConnectingRef.current = true;
    
    try {
      // Socket.IO 클라이언트 생성
      const socket = io(url, {
        transports: ['websocket', 'polling'], // WebSocket 우선, 폴링 폴백
        timeout: 20000, // 20초 타임아웃
        forceNew: true, // 새로운 연결 강제
        reconnection: false, // Socket.IO 자동 재연결 비활성화 (수동으로 관리)
        reconnectionAttempts: 0,
        reconnectionDelay: 0,
        reconnectionDelayMax: 0,
      });

      socketRef.current = socket;

      // 연결 성공
      socket.on('connect', () => {
        console.log('✅ Socket.IO 연결 성공!', socket.id);
        isConnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        lastErrorRef.current = 0;
        lastMessageTimeRef.current = Date.now(); // 연결 시점을 마지막 메시지 시간으로 설정
        setIsConnected(true);
        onOpen?.();
      });

      // 연결 실패
      socket.on('connect_error', (error) => {
        lastErrorRef.current = Date.now();
        isConnectingRef.current = false;
        
        console.error('❌ Socket.IO 연결 에러:', error);
        console.error('🔍 연결 URL:', url);
        console.error('🌍 현재 환경:', process.env.NODE_ENV);
        
        onError?.(new Event('connect_error'));
        
        // 수동 재연결 시도
        if (!isDestroyedRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = reconnectInterval * Math.pow(2, reconnectAttemptsRef.current - 1);
          
          console.log(`🔄 재연결 스케줄링: ${reconnectAttemptsRef.current}/${maxReconnectAttempts} (${delay}ms 후)`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (!isDestroyedRef.current) {
              console.log(`🔄 재연결 시도 (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
              connect();
            }
          }, delay);
        }
      });

      // 연결 해제
      socket.on('disconnect', (reason) => {
        console.log(`🔌 Socket.IO 연결 해제: ${reason}`);
        isConnectingRef.current = false;
        setIsConnected(false);
        onClose?.();

        // 정상 종료가 아니고 컴포넌트가 마운트된 상태에서만 재연결 시도
        if (reason !== 'io client disconnect' && !isDestroyedRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = reconnectInterval * Math.pow(2, reconnectAttemptsRef.current - 1);
          
          console.log(`🔄 재연결 스케줄링: ${reconnectAttemptsRef.current}/${maxReconnectAttempts} (${delay}ms 후)`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (!isDestroyedRef.current) {
              console.log(`🔄 재연결 시도 (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
              connect();
            }
          }, delay);
        }
      });

      // 메시지 수신
      socket.on('TOKEN_UPDATE', (data) => {
        try {
          lastMessageTimeRef.current = Date.now(); // 메시지 수신 시간 업데이트
          const message: AFMessage = typeof data === 'string' ? JSON.parse(data) : data;
          onTokenUpdateMessage?.(message);
        } catch (error) {
          console.error('❌ Socket.IO 메시지 파싱 실패:', error);
        }
      });

      // 에러 이벤트
      socket.on('error', (error) => {
        console.error('❌ Socket.IO 에러:', error);
        onError?.(new Event('socket_error'));
      });

    } catch (error) {
      lastErrorRef.current = Date.now();
      console.error('💥 Socket.IO 객체 생성 실패:', error);
      isConnectingRef.current = false;
    }
  }, [url, onOpen, onTokenUpdateMessage, onClose, onError, reconnectInterval, maxReconnectAttempts]);

  // 헬스체크 함수
  const performHealthCheck = useCallback(() => {
    if (isDestroyedRef.current) {
      return;
    }

    const socket = socketRef.current;
    const now = Date.now();
    const timeSinceLastMessage = now - lastMessageTimeRef.current;

    console.log(`🏥 WebSocket 헬스체크: 연결상태=${socket?.connected}, 마지막 메시지로부터 ${Math.round(timeSinceLastMessage / 1000)}초 경과`);

    // 연결이 끊어져 있거나 너무 오래 메시지를 받지 못한 경우 재연결 시도
    if (!socket?.connected || timeSinceLastMessage > healthCheckInterval * 2) {
      console.log('🔄 헬스체크 실패: 재연결 시도');
      if (socket?.connected) {
        socket.disconnect();
      }
      reconnectAttemptsRef.current = 0; // 헬스체크로 인한 재연결은 시도 횟수 리셋
      connect();
    }
  }, [healthCheckInterval, connect]);

  // 헬스체크 시작
  const startHealthCheck = useCallback(() => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
    }
    
    healthCheckIntervalRef.current = setInterval(performHealthCheck, healthCheckInterval);
    console.log(`🏥 WebSocket 헬스체크 시작: ${healthCheckInterval / 1000}초 간격`);
  }, [performHealthCheck, healthCheckInterval]);

  // 헬스체크 중지
  const stopHealthCheck = useCallback(() => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
      healthCheckIntervalRef.current = null;
      console.log('🏥 WebSocket 헬스체크 중지');
    }
  }, []);

  const disconnect = useCallback(() => {
    // 헬스체크 중지
    stopHealthCheck();
    
    // 재연결 타이머 정리
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Socket.IO 연결 정리
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    isConnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    lastErrorRef.current = 0;
    setIsConnected(false);
  }, [stopHealthCheck]);

  const sendMessage = useCallback((message: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('message', JSON.stringify(message));
    } else {
      console.warn('Socket.IO is not connected, cannot send message');
    }
  }, []);

  // URL이 변경될 때만 연결 시도
  useEffect(() => {
    isDestroyedRef.current = false;
    lastErrorRef.current = 0;
    
    // 초기 연결 시도 전에 짧은 지연
    const initialDelay = setTimeout(() => {
      if (!isDestroyedRef.current) {
        connect();
      }
    }, 1000);
    
    return () => {
      isDestroyedRef.current = true;
      clearTimeout(initialDelay);
      stopHealthCheck(); // 헬스체크 중지
      disconnect();
    };
  }, [connect, disconnect, stopHealthCheck, url]); // connect, disconnect를 의존성에서 제거하고 url만 유지

  // 연결 상태에 따른 헬스체크 관리
  useEffect(() => {
    if (isConnected) {
      startHealthCheck();
    } else {
      stopHealthCheck();
    }
    
    return () => {
      stopHealthCheck();
    };
  }, [isConnected, startHealthCheck, stopHealthCheck]);

  return {
    sendMessage,
    disconnect,
    reconnect: connect,
    isConnected,
    isConnecting: isConnectingRef.current,
    reconnectAttempts: reconnectAttemptsRef.current,
    lastMessageTime: lastMessageTimeRef.current,
    healthCheckInterval: healthCheckInterval
  };
}; 